import { ApolloClient, ApolloQueryResult, useQuery, useApolloClient } from '@apollo/client';
import { Button, Heading, Input, Stack, Textarea } from '@sixfold/common-ui';
import { isNil } from '@sixfold/typed-primitives';
import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { utils as xlsxUtils, writeFile as xlsxWriteFile } from 'xlsx';

import { VehicleMutation } from './vehicle_mutation_container';
import { CompaniesDropdownContainer, CompanySearchResult } from '../../company/containers/companies_dropdown_container';
import { Delay } from '../../components/delay';
import { InfiniteScroll } from '../../components/infinitescroll';
import { getNodes, loadMoreFromConnection, VehicleListQuery, VehicleListQueryVariables } from '../../lib/graphql';
import { Loader } from '../../lib/loader';
import NotFoundLicensePlates from '../components/not_found_license_plates';
import { VehiclesList, VehiclesListDataProps } from '../components/vehicles_list';
import { VehiclesMap } from '../components/vehicles_map';
import { missingLicensePlatesQuery, vehiclesQuery } from '../graphql';
import { formatReportFileName, processLicensePlateSearch } from '../utils';

import styles from './vehicle_list_container.module.css';

class LoadingContainer extends Loader<VehicleListQuery, VehiclesListDataProps, VehicleListQueryVariables> {}
const PAGINATION_PAGE_SIZE = 200;

type VehicleExcel = {
  vehicle_id: string;
  license_plate_number: string;
  company?: { company_name?: string | null } | null;
  created_at: string | null;
  status?: { timestamp: string | null; timeBasedStatus: string | null } | null;
  telematicsIntegrations: { telematics_integration_id: string }[];
};

const fetchAllVehicles = async (
  client: ApolloClient<object>,
  variables: VehicleListQueryVariables,
): Promise<VehicleExcel[]> => {
  const all: VehicleExcel[] = [];
  let cursor: string | undefined = undefined;
  let hasNext = true;

  while (hasNext) {
    const response: ApolloQueryResult<VehicleListQuery> = await client.query<
      VehicleListQuery,
      VehicleListQueryVariables
    >({
      query: vehiclesQuery,
      variables: { ...variables, cursor, limit: 1000 },
      fetchPolicy: 'network-only',
    });

    const nodes = getNodes(response.data.vehicles) as VehicleExcel[];
    all.push(...nodes);
    hasNext = response.data.vehicles?.pageInfo.hasNextPage ?? false;
    cursor = response.data.vehicles?.pageInfo.endCursor ?? undefined;
  }

  return all;
};

const getVehicleStatusLabel = (timeBasedStatus: string | null | undefined): string => {
  if (timeBasedStatus === 'ACTIVE') return 'Active';
  if (timeBasedStatus === 'INACTIVE') return 'Inactive';
  return '';
};

const buildVehicleExcelRows = (vehicles: VehicleExcel[]): string[][] => {
  const header = ['ID', 'License plate', 'Company', 'Created at', 'Last status at', 'Vehicle Status', 'Integrations'];
  const rows = vehicles.map((v) => {
    return [
      v.vehicle_id,
      v.license_plate_number,
      v.company?.company_name ?? '',
      v.created_at ?? '',
      v.status?.timestamp ?? '',
      getVehicleStatusLabel(v.status?.timeBasedStatus),
      v.telematicsIntegrations.map((ti) => ti.telematics_integration_id).join(', '),
    ];
  });
  return [header, ...rows];
};

export const VehicleListContainer: React.FunctionComponent = () => {
  const [selectedCompany, setSelectedCompany] = useState<CompanySearchResult | null>(null);
  const [committedCompany, setCommittedCompany] = useState<CompanySearchResult | null>(null);

  const [integrationId, setIntegrationId] = useState<string | null>(null);
  const [committedIntegrationId, setCommittedIntegrationId] = useState<string | null>(null);

  const [licensePlate, setLicensePlate] = useState<string | null>(null);
  const [committedLicensePlate, setCommittedLicensePlate] = useState<string | null>(null);
  const [missingPlates, setMissingPlates] = useState<string[]>([]);
  const [triggerVehicleQuery, setTriggerVehicleQuery] = useState(false);
  const [hasRunMissingQuery, setHasRunMissingQuery] = useState(false);

  const [isExporting, setIsExporting] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const client = useApolloClient();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const companyIdParam = params.get('company_id');
    const integrationIdParam = params.get('integration_id');
    const licensePlateParam = params.get('license_plate');

    if (companyIdParam !== null) {
      setSelectedCompany({ company_id: companyIdParam } as unknown as CompanySearchResult);
      setCommittedCompany({ company_id: companyIdParam } as unknown as CompanySearchResult);
    }

    if (integrationIdParam !== null) {
      setIntegrationId(integrationIdParam);
      setCommittedIntegrationId(integrationIdParam);
    }

    if (licensePlateParam !== null) {
      setLicensePlate(licensePlateParam);
      setCommittedLicensePlate(licensePlateParam);
    }

    setTriggerVehicleQuery(true);
  }, [location.search]);

  const errors = {
    notEnoughCharacters: 'Min 2 characters per plate.',
    tooManyPlates: 'Max 1000 license plates per search.',
    noLicensePlates: 'Please enter at least one license plate.',
  };

  const getLicensePlateError = (input: string | null | undefined): string | undefined => {
    if (isNil(input)) return undefined;

    const plates = input
      .split(/[\n,]/)
      .map((lp) => lp.trim())
      .filter((lp) => lp.length > 0);

    if (plates.length === 0) {
      return errors.noLicensePlates;
    }

    if (plates.length > 1000) {
      return errors.tooManyPlates;
    }

    const hasShortPlate = plates.some((lp) => lp.length < 2);
    if (hasShortPlate) {
      return errors.notEnoughCharacters;
    }

    return undefined;
  };

  const currentError = getLicensePlateError(licensePlate);

  const getLicensePlateArray = (input: string | null | undefined): string[] => {
    if (isNil(input)) return [];

    return input
      .split(/[\n,]/)
      .map((lp) => lp.trim())
      .filter((lp) => lp.length >= 2);
  };

  const handleSearch = () => {
    if (currentError) return;

    setCommittedLicensePlate(licensePlate);
    setCommittedIntegrationId(integrationId);
    setCommittedCompany(selectedCompany);
    setTriggerVehicleQuery(true);
    setMissingPlates([]);
    setHasRunMissingQuery(false);

    const params = new URLSearchParams();
    if (selectedCompany?.company_id) {
      params.set('company_id', selectedCompany.company_id);
    }
    if (integrationId) {
      params.set('integration_id', integrationId);
    }
    if (licensePlate) {
      params.set('license_plate', licensePlate);
    }

    void navigate(`/vehicles${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const isValidCommittedLicensePlateSearch =
    getLicensePlateError(committedLicensePlate) === undefined && committedLicensePlate !== null;

  const handleExport = useCallback(
    async (apolloClient: ApolloClient<object>) => {
      try {
        setIsExporting(true);

        const variables: VehicleListQueryVariables = {
          limit: 1000,
          companyId: committedCompany?.company_id ?? undefined,
          licensePlateSearch: isValidCommittedLicensePlateSearch
            ? processLicensePlateSearch(committedLicensePlate)
            : undefined,
          integrationIds: committedIntegrationId ? [committedIntegrationId] : undefined,
          cursor: undefined,
        } as VehicleListQueryVariables;

        const vehicles = await fetchAllVehicles(apolloClient, variables);
        if (vehicles.length === 0) {
          alert('No vehicles to export');
          return;
        }

        const excelRows = buildVehicleExcelRows(vehicles);
        const fileName = formatReportFileName('vehicles-search', 'en-US', new Date());
        const licensePlateArray = getLicensePlateArray(committedLicensePlate);
        const maxRows = Math.max(1, licensePlateArray.length, missingPlates.length);

        const filtersData: (string | null)[][] = [
          ['Search run at', 'Company ID', 'Integration ID', 'License plates', 'Missing plates'],
        ];

        for (let i = 0; i < maxRows; i++) {
          filtersData.push([
            i === 0 ? new Date().toString() : '',
            i === 0 ? (committedCompany?.company_id ?? '') : '',
            i === 0 ? (committedIntegrationId ?? '') : '',
            i < licensePlateArray.length ? licensePlateArray[i] : '',
            i < missingPlates.length ? missingPlates[i] : i === 0 && missingPlates.length === 0 ? 'None' : '',
          ]);
        }
        const wb = xlsxUtils.book_new();
        const filtersSheet = xlsxUtils.aoa_to_sheet(filtersData);
        xlsxUtils.book_append_sheet(wb, filtersSheet, 'Filters');

        const vehiclesSheet = xlsxUtils.aoa_to_sheet(excelRows);
        xlsxUtils.book_append_sheet(wb, vehiclesSheet, 'Vehicles');

        xlsxWriteFile(wb, `${fileName}`);
      } catch (err) {
        console.error('Export failed', err);
      } finally {
        setIsExporting(false);
      }
    },
    [
      committedCompany,
      committedLicensePlate,
      committedIntegrationId,
      isValidCommittedLicensePlateSearch,
      missingPlates,
    ],
  );

  const result = useQuery<VehicleListQuery, VehicleListQueryVariables>(vehiclesQuery, {
    skip: !triggerVehicleQuery,
    variables: {
      limit: PAGINATION_PAGE_SIZE,
      companyId: committedCompany?.company_id ?? undefined,
      licensePlateSearch: isValidCommittedLicensePlateSearch
        ? processLicensePlateSearch(committedLicensePlate)
        : undefined,
      integrationIds: committedIntegrationId ? [committedIntegrationId] : undefined,
    },
  });

  useQuery(missingLicensePlatesQuery, {
    skip: !(
      triggerVehicleQuery &&
      !hasRunMissingQuery &&
      getLicensePlateError(committedLicensePlate) === undefined &&
      committedLicensePlate !== null
    ),
    variables: {
      licensePlateSearch: isValidCommittedLicensePlateSearch
        ? processLicensePlateSearch(committedLicensePlate)
        : undefined,
      companyNameSearch: committedCompany?.company_name ?? undefined,
      integrationIds: committedIntegrationId ? [committedIntegrationId] : undefined,
    },
    onCompleted: (data: { missingLicensePlates?: { missingLicensePlates?: string[] } }) => {
      setMissingPlates(data?.missingLicensePlates?.missingLicensePlates ?? []);
      setHasRunMissingQuery(true);
    },
  });

  return (
    <VehicleMutation>
      {({ deleteVehicles }) => {
        const vehicles = getNodes(result.data?.vehicles);
        const exportDisabled = isExporting || !triggerVehicleQuery || vehicles.length === 0;
        return (
          <Stack
            direction="row"
            gap="medium"
            wrap={false}
            className="vehicles"
            style={{ height: 'calc(100vh - 155px)' }}>
            <div
              className="vehicles__list"
              style={{ flex: '0 0 62.5%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <Heading size="large">Vehicles</Heading>
              <Fragment>
                <Stack direction="row" className={styles.searchFormStack} justifyContent="start">
                  <div className={styles.licensePlateColumn}>
                    <Textarea
                      label="License plates"
                      labelPosition="hidden"
                      name="license-plates"
                      placeholder="License plates (one per line or comma separated)..."
                      value={licensePlate === null ? '' : licensePlate}
                      className={styles.licensePlateTextArea}
                      onChange={({ value }) => {
                        setLicensePlate(value === '' ? null : String(value));
                      }}
                    />
                  </div>
                  <div className={styles.controlsColumn}>
                    <div className={styles.controlsRow}>
                      <Input
                        label="Integration id"
                        labelPosition="hidden"
                        name="integration-id"
                        placeholder="Integration id..."
                        value={integrationId === null ? '' : integrationId}
                        onChange={({ value }) => {
                          setIntegrationId(value === '' ? null : value);
                        }}
                        className={styles.integrationInput}
                      />
                      <div className={styles.companyDropdown}>
                        <CompaniesDropdownContainer
                          inputProps={{ placeholder: 'All Companies' }}
                          onSelectCompany={(company) => {
                            setSelectedCompany(company);
                            if (company === null) {
                              setCommittedCompany(null);
                            }
                          }}
                          selectedCompany={selectedCompany}
                        />
                      </div>
                      <div className={styles.buttonsRow}>
                        <Button
                          kind="primary"
                          iconStart="small-search"
                          onClick={handleSearch}
                          disabled={!!currentError}
                          className={styles.searchButton}>
                          Search
                        </Button>

                        <Button
                          iconStart="small-download"
                          onClick={() => handleExport(client)}
                          loading={isExporting}
                          disabled={exportDisabled}
                          title="Export to Excel"
                          className={styles.exportButton}>
                          Export
                        </Button>
                      </div>
                    </div>
                  </div>
                </Stack>
              </Fragment>

              {currentError && (
                <div className={styles.errorMessage}>
                  <Delay showAfterMs={1_000}>
                    <span style={{ color: 'red' }}>{currentError}</span>
                  </Delay>
                </div>
              )}
              {missingPlates.length > 0 &&
                getLicensePlateArray(committedLicensePlate).length !== missingPlates.length && (
                  <NotFoundLicensePlates notFoundPlates={missingPlates} />
                )}
              <LoadingContainer
                result={result}
                mapData={({ data }) => ({ data: { vehicles: getNodes(data.vehicles) } })}>
                {({ data }) => (
                  <InfiniteScroll
                    key={`table-${committedLicensePlate || ''}-${committedIntegrationId || ''}-${committedCompany?.company_id || ''}`}
                    className="vehicles__list__table"
                    triggerOnWindow={false}
                    loadMoreEntries={() => loadMoreFromConnection(vehiclesQuery, result, ['vehicles'])}>
                    <VehiclesList
                      data={data}
                      search={committedLicensePlate ?? undefined}
                      company={committedCompany ?? undefined}
                      onDeleteVehicles={async (vehicleIdsToDelete) => {
                        if (!committedCompany?.company_id) {
                          return;
                        }

                        await deleteVehicles(vehicleIdsToDelete, committedCompany?.company_id);
                      }}
                    />
                  </InfiniteScroll>
                )}
              </LoadingContainer>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <LoadingContainer
                result={result}
                mapData={({ data }) => ({ data: { vehicles: getNodes(data.vehicles) } })}>
                {({ data }) => <VehiclesMap data={{ vehicles: data.vehicles }} />}
              </LoadingContainer>
            </div>
          </Stack>
        );
      }}
    </VehicleMutation>
  );
};
